import { NodeViewProps } from '@tiptap/core'
import { NodeViewWrapper } from '@tiptap/react'
import React, { startTransition, useEffect, useRef, useState } from 'react'
import {
  cleanImagePercentage,
  generateImageStyles,
  getImagePercentage,
  getImageSize,
} from '../utils/generateImageStyles'

const toCssProperties = (styles: string): Record<string, any> =>
  styles.split(';').reduce((acc: Record<string, any>, style: string) => {
    const [keyRaw, value] = style.split(':')
    const key = keyRaw.replace(/-./g, (m) => m[1].toUpperCase())
    if (key && value) acc[key.trim()] = value.trim()
    return acc
  }, {})

export const EditorImageComponent = ({ node, selected, updateAttributes }: NodeViewProps) => {
  const { src, alt, title, height, float, alignment, size } = node.attrs
  const containerRef = useRef<HTMLImageElement>(null)
  const [percentage, setPercentage] = useState<number>(getImagePercentage(size))

  useEffect(() => {
    if (percentage !== getImagePercentage(size)) setPercentage(getImagePercentage(size))
  }, [percentage, size])

  return (
    <NodeViewWrapper
      className={`img-wrapper ${selected ? 'ProseMirror-selectednode' : ''}`.trim()}
      style={{
        ...toCssProperties(generateImageStyles(node.attrs)),
        height: 200,
      }}
    >
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          position: 'relative',
          maxWidth: '100%',
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          borderRadius: '3px',
        }}
      >
        <img
          src={src}
          alt={alt}
          title={title}
          width={`${percentage}%`}
          height={height}
          {...{
            ...node.attrs,
            'data-float': float,
            'data-size': size,
            'data-alignment': alignment,
          }}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            objectFit: 'contain',
            imageOrientation: 'none',
            transform: 'translate(-50%, -50%)',
            height: '100%',
          }}
          draggable="true"
          data-drag-handle=""
        />
        {!float && (
          <span
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              top: 0,
              pointerEvents: 'none',
            }}
          >
            {alignment !== 'left' && (
              <div
                className="richMedia-resize-handle-left"
                style={{
                  position: 'absolute',
                  userSelect: 'none',
                  width: 24,
                  height: '100%',
                  top: 0,
                  left: '-13px',
                  cursor: 'col-resize',
                  zIndex: 99,
                  pointerEvents: 'auto',
                }}
                onMouseDown={(mouseDownEvent: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                  if (!containerRef.current) return
                  const startPercentage = percentage
                  const startMouseX = mouseDownEvent.clientX
                  const containerWidth = containerRef.current.offsetWidth / 2
                  let newPercentage = startPercentage

                  function handleMouseMove(mouseMoveEvent: any) {
                    // if left direction make bigger else make smaller
                    const deltaX = startMouseX - mouseMoveEvent.clientX
                    const deltaPercentage = (deltaX / containerWidth) * 100
                    newPercentage = startPercentage + deltaPercentage
                    startTransition(() => {
                      setPercentage(cleanImagePercentage(newPercentage))
                    })
                  }
                  function onMouseUp() {
                    document.body.removeEventListener('mousemove', handleMouseMove)
                    startTransition(() => {
                      updateAttributes({ size: getImageSize(newPercentage) })
                    })
                  }

                  document.body.addEventListener('mousemove', handleMouseMove)
                  document.body.addEventListener('mouseup', onMouseUp, { once: true })
                }}
              >
                <div />
              </div>
            )}
            {alignment !== 'right' && (
              <div
                className="richMedia-resize-handle-right"
                style={{
                  position: 'absolute',
                  userSelect: 'none',
                  width: 24,
                  height: '100%',
                  top: 0,
                  right: '-13px',
                  cursor: 'col-resize',
                  zIndex: 99,
                  pointerEvents: 'auto',
                }}
                onMouseDown={(mouseDownEvent: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                  if (!containerRef.current) return
                  const startPercentage = percentage
                  const startMouseX = mouseDownEvent.clientX
                  const containerWidth = containerRef.current.offsetWidth / 2
                  let newPercentage = startPercentage

                  function handleMouseMove(mouseMoveEvent: any) {
                    // if left direction make bigger else make smaller
                    const deltaX = mouseMoveEvent.clientX - startMouseX
                    const deltaPercentage = (deltaX / containerWidth) * 100
                    newPercentage = startPercentage + deltaPercentage
                    startTransition(() => {
                      setPercentage(cleanImagePercentage(newPercentage))
                    })
                  }
                  function onMouseUp() {
                    document.body.removeEventListener('mousemove', handleMouseMove)
                    startTransition(() => {
                      updateAttributes({ size: getImageSize(newPercentage) })
                    })
                  }

                  document.body.addEventListener('mousemove', handleMouseMove)
                  document.body.addEventListener('mouseup', onMouseUp, { once: true })
                }}
              >
                <div contentEditable="false" />
              </div>
            )}
          </span>
        )}
      </div>
    </NodeViewWrapper>
  )
}
